import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import { Box, IconButton, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchClient, fetchProductionPark } from "store/appSlice";
import { selectClient, selectMe, selectProductionPark } from "store/selectors";
import { ProductionPark } from "api/models/ProductionPark";
import { Battery } from "api/models/Battery";
import { ProductionParkType } from "api/models/enums/ProductionParkType";
import {
  deleteProductionPark,
  updateProductionPark,
  getBatteriesByProductionParkId,
  createBattery,
  updateBattery,
  deleteBattery,
  linkConsumptionForecastTemplate,
} from "api/resources/productionPark";
import { ProtectedPaths } from "routes";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ProductionParkForm from "../Components/ProductionParkForm";
import { useProductionParkForm } from "../Hooks/useProductionParkForm";
import { getTabsByRoleRegistry } from "../../../../../helpers/tabs";

const EditProductionPark: React.FC = () => {
  const { productionParkId } = useParams<{ productionParkId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const productionPark = useAppSelector(selectProductionPark);
  const client = useAppSelector(selectClient);
  const me = useAppSelector(selectMe);
  const isAdmin = me?.roles.some(
    (role) => role.name === "ROLE_ADMIN" && role.isCurrentRole,
  );
  const [snackbar, setSnackbar] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batteries, setBatteries] = useState<Battery[]>([]);
  const [batteriesToDelete, setBatteriesToDelete] = useState<number[]>([]);
  const initialFormData: ProductionPark = {
    id: null,
    name: "",
    type: ProductionParkType.SOLAR,
    code: "",
    aiolosCode: "",
    location: "",
    priceArea: "",
    deliveryType: "",
    clientId: null,
    productionParkComponents: [],
    meteringPoints: [],
    isBigProducer: false,
  };

  const {
    formData,
    setFormData,
    selectedCountry,
    setSelectedCountry,
    selectedClients,
    setSelectedClients,
    handleChange,
    handleRemoveComponent,
    handleAddComponent,
    handleCountrySelect,
  } = useProductionParkForm(initialFormData);

  const handleBatteryChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const { name, value } = e.target;
    setBatteries((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [name]: value,
      };
      return next;
    });
  };

  const handleRemoveBattery = (index: number) => {
    const battery = batteries[index];
    if (battery.id) {
      setBatteriesToDelete((prev) => [...prev, battery.id]);
    }
    setBatteries((prevBatteries) =>
      prevBatteries.filter((_, i) => i !== index),
    );
  };

  const handleAddBattery = () => {
    setBatteries((prevBatteries) => [
      ...prevBatteries,
      {
        productionParkId: Number(productionParkId),
        minSoc: 0,
        maxSoc: 0,
        capacity: 0,
        inverterCapacity: 0,
        maxCharge: 0,
        maxDischarge: 0,
        chargingEfficiency: 0,
        dischargingEfficiency: 0,
      },
    ]);
  };

  const handleCloneBattery = (index: number) => {
    const batteryToClone = batteries[index];
    const clonedBattery = {
      ...batteryToClone,
      id: undefined,
    };
    setBatteries((prevBatteries) => [...prevBatteries, clonedBattery]);
  };

  useEffect(() => {
    if (productionParkId) {
      dispatch(fetchProductionPark(Number(productionParkId)));
      getBatteriesByProductionParkId(Number(productionParkId))
        .then((response) => {
          setBatteries(response.data || []);
        })
        .catch((error) => {
          console.error("Failed to fetch batteries:", error);
        });
    }
  }, [productionParkId, dispatch]);

  useEffect(() => {
    if (productionPark) {
      setFormData(productionPark);
      setSelectedCountry({
        code: productionPark.priceArea,
        label: productionPark.priceArea,
        phone: productionPark.priceArea,
      });
    }

    if (productionPark?.clientId && isAdmin) {
      dispatch(fetchClient(productionPark.clientId?.toString()));
    }
  }, [productionPark, dispatch, setFormData, setSelectedCountry, isAdmin]);

  useEffect(() => {
    if (client) {
      const clientName =
        client.type === "BUSINESS"
          ? client.companyName
          : `${client.firstName} ${client.lastName}`;
      setSelectedClients([{ title: clientName, value: client.id }]);
    }
  }, [client, setSelectedClients]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const clientId = selectedClients[0]?.value;
    const meteringPointIds = formData.meteringPoints
      .filter((point) => point !== null)
      .map((point) => point.id);

    const { ...postData } = {
      ...formData,
      clientId,
      meteringPoints: meteringPointIds,
    };

    try {
      await updateProductionPark(Number(productionParkId), postData);

      await linkConsumptionForecastTemplate(
        Number(productionParkId),
        formData.consumptionForecastTemplate?.id ?? null,
      );

      for (const batteryId of batteriesToDelete) {
        await deleteBattery(batteryId);
      }

      for (const battery of batteries) {
        const batteryData = {
          ...battery,
          externalId: battery.externalId || undefined,
          minSoc: battery.minSoc === "" ? 0 : battery.minSoc,
          maxSoc: battery.maxSoc === "" ? 0 : battery.maxSoc,
          capacity: battery.capacity === "" ? 0 : battery.capacity,
          inverterCapacity:
            battery.inverterCapacity === "" ? 0 : battery.inverterCapacity,
          maxCharge: battery.maxCharge === "" ? 0 : battery.maxCharge,
          maxDischarge: battery.maxDischarge === "" ? 0 : battery.maxDischarge,
          chargingEfficiency:
            battery.chargingEfficiency === "" ? 0 : battery.chargingEfficiency,
          dischargingEfficiency:
            battery.dischargingEfficiency === ""
              ? 0
              : battery.dischargingEfficiency,
        };
        if (battery.id) {
          await updateBattery(battery.id, batteryData);
        } else {
          await createBattery(batteryData);
        }
      }

      setBatteriesToDelete([]);

      setSnackbar({
        type: "success",
        message: "Production park submitted successfully",
      });

      getBatteriesByProductionParkId(Number(productionParkId))
        .then((response) => {
          setBatteries(response.data || []);
        })
        .catch((error) => {
          console.error("Failed to refresh batteries:", error);
        });
    } catch (error) {
      setSnackbar({
        type: "error",
        message: error.response?.data.message,
      });
    }
  };

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const handleDelete = async () => {
    try {
      await deleteProductionPark(Number(productionParkId));
      setSnackbar({
        type: "success",
        message: "Production park deleted successfully",
      });
      navigate(`/${ProtectedPaths.ProductionParks}`);
    } catch (error) {
      setSnackbar({
        type: "error",
        message: error.response?.data.message,
      });
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  return (
    <MainLayout
      tabs={getTabsByRoleRegistry()}
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", paddingBottom: 3 }}>
        <IconButton sx={{ mr: "1rem" }} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h5">
          Edit production park
        </Typography>
      </Box>
      <ProductionParkForm
        formData={formData}
        setFormData={setFormData}
        selectedCountry={selectedCountry}
        handleCountrySelect={handleCountrySelect}
        selectedClients={selectedClients}
        setSelectedClients={setSelectedClients}
        handleChange={handleChange}
        handleRemoveComponent={handleRemoveComponent}
        handleAddComponent={handleAddComponent}
        handleSubmit={handleSubmit}
        deleteModalOpen={deleteModalOpen}
        openDeleteModal={openDeleteModal}
        closeDeleteModal={closeDeleteModal}
        handleDelete={handleDelete}
        showDeleteButton={true}
        productionParkId={Number(productionParkId)}
        setSnackbar={setSnackbar}
        productionParkType={productionPark?.type}
        isEditMode
        batteries={batteries}
        handleBatteryChange={handleBatteryChange}
        handleRemoveBattery={handleRemoveBattery}
        handleAddBattery={handleAddBattery}
        handleCloneBattery={handleCloneBattery}
      />
    </MainLayout>
  );
};

export default EditProductionPark;
